import React, { useEffect, useState } from "react";
import { Loader2, ArrowLeft, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import WelcomeStep from "@/components/theatre/WelcomeStep";
import StageManagersStep from "@/components/theatre/StageManagersStep";
import DoneStep from "@/components/theatre/DoneStep";
import { useTeamFeatures, TheatreFeatureSwitches } from "@/components/profile/TheatreFeatureToggles";

const STEPS = [
  { key: "welcome", title: "Welcome to the Theatre plan", desc: "Here's what your team gets — this takes about a minute." },
  { key: "sm", title: "Add your stage managers", desc: "They can draft Rehearsal and Show Notes for your approval." },
  { key: "features", title: "Choose your program features", desc: "Switch off anything your program doesn't use." },
  { key: "done", title: "You're all set", desc: "" },
];

const picksFrom = (s) => Object.fromEntries((s.members || []).filter((m) => m.subgroup_ids.includes(s.stage_manager_group?.id))
  .map((m) => [m.id, m.subgroup_ids.filter((g) => g !== s.stage_manager_group.id)]));

// Theatre Welcome Setup wizard: Welcome → Stage managers → Program features → Done.
export default function TheatreSetupWizard({ open, onOpenChange, status, onChanged }) {
  const [step, setStep] = useState(0);
  const [picks, setPicks] = useState({});
  const [busy, setBusy] = useState(false);
  const [features, toggle] = useTeamFeatures();
  useEffect(() => { if (open && status) { setStep(0); setPicks(picksFrom(status)); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const next = async () => {
    setBusy(true);
    try {
      if (STEPS[step].key === "sm") {
        await api.put("/coach/theatre-setup/stage-managers", { assignments: Object.entries(picks).map(([member_id, production_ids]) => ({ member_id, production_ids })) });
        toast.success(`${Object.keys(picks).length} stage manager${Object.keys(picks).length === 1 ? "" : "s"} saved`);
        onChanged();
      }
      if (STEPS[step].key === "features") { await api.post("/coach/theatre-setup/complete"); onChanged(); }
      setStep((s) => s + 1);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const s = STEPS[step];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92dvh] max-w-2xl overflow-y-auto" data-testid="theatre-setup-dialog">
        <div className="flex gap-1.5" data-testid="theatre-setup-progress">
          {STEPS.map((x, i) => <span key={x.key} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-rose-500" : "bg-slate-200"}`} />)}
        </div>
        <DialogHeader>
          <p className="text-xs font-bold uppercase tracking-wider text-rose-600">Step {step + 1} of {STEPS.length}</p>
          <DialogTitle className="font-heading text-2xl" data-testid="theatre-setup-title">{s.title}</DialogTitle>
          {s.desc && <DialogDescription>{s.desc}</DialogDescription>}
        </DialogHeader>
        {s.key === "welcome" && <WelcomeStep />}
        {s.key === "sm" && status && <StageManagersStep status={status} picks={picks} setPicks={setPicks} />}
        {s.key === "features" && <TheatreFeatureSwitches f={features} toggle={toggle} />}
        {s.key === "done" && <DoneStep onNavigate={() => onOpenChange(false)} />}
        <div className="mt-2 flex items-center justify-between gap-2">
          {step > 0 && s.key !== "done"
            ? <Button variant="ghost" className="rounded-full" onClick={() => setStep(step - 1)} data-testid="theatre-setup-back"><ArrowLeft className="mr-1 h-4 w-4" /> Back</Button>
            : <span />}
          {s.key === "done" ? (
            <Button className="rounded-full bg-slate-900 hover:bg-slate-800" onClick={() => onOpenChange(false)} data-testid="theatre-setup-finish">Close</Button>
          ) : (
            <Button className="rounded-full bg-slate-900 hover:bg-slate-800" disabled={busy} onClick={next} data-testid="theatre-setup-next">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {{ welcome: "Get started", sm: "Save & continue", features: "Finish setup" }[s.key]} {!busy && <ArrowRight className="ml-1 h-4 w-4" />}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
